import { ScrollArea } from "@/components/primitives/scroll-area";
import { RefObject } from "react";

/**
 * Container component for media attachments
 * Provides scrolling functionality and layout for the media attachment list
 *
 * How it works:
 * 1. Creates a scrollable container with proper overflow handling
 * 2. Accepts and forwards ref objects for scroll position management
 * 3. Renders children components within the scrollable area
 * 4. Uses flex-col layout for proper content organization
 *
 * Key features:
 * - Touch-friendly scrolling with WebkitOverflowScrolling
 * - Consistent scrolling behavior across devices
 * - Proper overflow handling and containment
 * - Fade-in animation for smooth appearance
 * - Maintains scroll position during content updates
 *
 * Context:
 * This component is used by MediaAttachmentList to provide the scrollable
 * container for all media attachments. It follows the same pattern as
 * FileAttachmentContainer and LinkContainer for consistency.
 */
interface MediaAttachmentContainerProps {
  scrollerRef: RefObject<HTMLDivElement>;
  attachmentsGridRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

export default function MediaAttachmentContainer({
  scrollerRef,
  attachmentsGridRef,
  children,
}: MediaAttachmentContainerProps) {
  return (
    <div
      ref={scrollerRef}
      style={{ WebkitOverflowScrolling: "touch" }}
      className="relative size-full flex-1 flex-col justify-end overflow-y-auto overflow-x-hidden fade-in"
    >
      <ScrollArea className="h-full flex-1 p-1">
        <div
          ref={attachmentsGridRef}
          style={{ WebkitOverflowScrolling: "touch" }}
          className="custom-scroll custom-scroll-x relative flex size-full flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
        >
          {children}
        </div>
      </ScrollArea>
    </div>
  );
}
